import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageToggle from "../components/app/LanguageToggle";
import AuthImage from "../components/app/AuthImage";
import Logo from "../components/app/Logo";
import { SPECIALTIES, SPECIALTY_LABEL } from "./FindCoaches";
import {
  Star, MapPin, Filter, ArrowLeft, Map as MapIcon, LayoutGrid,
  Search as SearchIcon, Sparkles, Trophy, X, Locate, Zap,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Custom emerald pin marker (no images)
const buildIcon = (isPro = false) => L.divIcon({
  className: "tactiq-pin",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -34],
  html: `
    <div class="tactiq-pin-inner ${isPro ? "is-pro" : ""}">
      <span class="tactiq-pin-dot"></span>
      <span class="tactiq-pin-ring"></span>
    </div>
  `,
});

// Gold-amber pin for academies (visually distinct)
const buildAcademyIcon = (isPro = false) => L.divIcon({
  className: "tactiq-pin tactiq-pin-academy",
  iconSize: [40, 40],
  iconAnchor: [20, 38],
  popupAnchor: [0, -36],
  html: `
    <div class="tactiq-pin-inner is-academy ${isPro ? "is-pro" : ""}">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="color:#FFB300;">
        <rect x="3" y="9" width="18" height="12" /><path d="M12 9V4l9 5" /><path d="M12 4l-9 5" /><path d="M12 14h.01" />
      </svg>
      <span class="tactiq-pin-ring" style="border-color:#FFB300;"></span>
    </div>
  `,
});

function FitToBounds({ points, focus, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (focus && radiusKm) {
      // Nearby mode — fit to a bounding box around the focus point spanning
      // the active radius so the user sees the whole search area.
      const degLat = radiusKm / 111;
      const degLng = radiusKm / (111 * Math.cos((focus.lat * Math.PI) / 180) || 1);
      const bounds = L.latLngBounds(
        [focus.lat - degLat, focus.lng - degLng],
        [focus.lat + degLat, focus.lng + degLng],
      );
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: true, duration: 0.9 });
      return;
    }
    if (focus) {
      map.flyTo([focus.lat, focus.lng], 14, { animate: true, duration: 1.0 });
      return;
    }
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 11, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [points, focus, radiusKm, map]);
  return null;
}

// Lightweight Nominatim autocomplete for location search (e.g. "Младост 1")
function useNominatim(query, lang = "bg") {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) { setResults([]); return; }
    const ctrl = new AbortController();
    const tid = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=6&addressdetails=1&accept-language=${lang}&q=${encodeURIComponent(q)}`;
        const r = await fetch(url, {
          signal: ctrl.signal,
          headers: { "Accept": "application/json" },
        });
        const data = await r.json();
        setResults((data || []).map((x) => ({
          id: x.place_id,
          name: x.display_name,
          short: (x.address && (x.address.neighbourhood || x.address.suburb || x.address.quarter || x.address.city_district || x.address.city || x.address.town || x.address.village)) || x.display_name?.split(",")?.[0],
          lat: parseFloat(x.lat),
          lng: parseFloat(x.lon),
        })));
      } catch (_) {}
      setLoading(false);
    }, 350);
    return () => { clearTimeout(tid); ctrl.abort(); };
  }, [query, lang]);
  return { results, loading };
}

export default function CoachesMap() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [kind, setKind] = useState("coaches"); // "coaches" | "academies" | "both"
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");           // coach-name search
  const [specialty, setSpecialty] = useState("");
  const [locQuery, setLocQuery] = useState(""); // location autocomplete input
  const [focus, setFocus] = useState(null);     // { lat, lng, label }
  const [showSuggest, setShowSuggest] = useState(false);
  const [priceRange, setPriceRange] = useState(""); // "" | "under30" | "30-60" | "60-100" | "100plus"
  const [ageGroup, setAgeGroup] = useState("");     // "" | "kids" | "youth" | "adult" | "pro"
  const [radiusKm, setRadiusKm] = useState(null);   // null | 5 | 10 | 25 | 50 — only applied when `focus` is set
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState("");
  const { results: locSuggestions, loading: locLoading } = useNominatim(locQuery, lang);
  const locInputRef = useRef(null);

  // Haversine — great-circle distance in km between two lat/lng points.
  const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (specialty) params.specialty = specialty;
      const [coachRes, academyRes] = await Promise.all([
        api.get("/public/coaches", { params }),
        api.get("/public/academies", { params: q ? { q } : {} }).catch(() => ({ data: [] })),
      ]);
      setCoaches(Array.isArray(coachRes.data) ? coachRes.data : (coachRes.data?.coaches || []));
      setAcademies(Array.isArray(academyRes.data) ? academyRes.data : (academyRes.data?.academies || academyRes.data || []));
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [specialty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tid = setTimeout(load, 350);
    return () => clearTimeout(tid);
    // eslint-disable-next-line
  }, [q]);

  // close autocomplete on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (locInputRef.current && !locInputRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoErr(lang === "bg" ? "Геолокацията не се поддържа от браузъра" : "Geolocation not supported");
      return;
    }
    setGeoBusy(true);
    setGeoErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFocus({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: lang === "bg" ? "Моята локация" : "My location",
        });
        setLocQuery("");
        setShowSuggest(false);
        // Auto-activate a 25km radius for instant "coaches near me" view
        if (!radiusKm) setRadiusKm(25);
        setGeoBusy(false);
      },
      (err) => {
        setGeoBusy(false);
        setGeoErr(
          err?.code === 1
            ? (lang === "bg" ? "Достъпът до локацията е отказан" : "Location permission denied")
            : (lang === "bg" ? "Не може да се определи локацията" : "Could not determine location")
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const clearNearby = () => {
    setFocus(null);
    setRadiusKm(null);
    setGeoErr("");
  };

  // Coaches with coordinates — fallback default Sofia center; client-side price/age/radius filters
  const points = useMemo(() => {
    const inPrice = (rate) => {
      if (!priceRange) return true;
      if (rate == null) return false;
      if (priceRange === "under30") return rate < 30;
      if (priceRange === "30-60") return rate >= 30 && rate <= 60;
      if (priceRange === "60-100") return rate > 60 && rate <= 100;
      if (priceRange === "100plus") return rate > 100;
      return true;
    };
    const inAge = (c) => {
      if (!ageGroup) return true;
      const tags = (c.age_groups || []);
      if (tags.length === 0) return true; // unset = match all (don't penalise older profiles)
      return tags.includes(ageGroup);
    };
    const withDistance = coaches
      .filter((c) => typeof c.location_lat === "number" && typeof c.location_lng === "number")
      .filter((c) => inPrice(c.hourly_rate))
      .filter(inAge)
      .map((c) => {
        const distance_km = focus
          ? Number(haversineKm(focus.lat, focus.lng, c.location_lat, c.location_lng).toFixed(1))
          : null;
        return { ...c, lat: c.location_lat, lng: c.location_lng, distance_km };
      });
    const filtered = (focus && radiusKm)
      ? withDistance.filter((c) => c.distance_km != null && c.distance_km <= radiusKm)
      : withDistance;
    // When in nearby mode, sort by distance ascending so closest renders first
    if (focus && radiusKm) filtered.sort((a, b) => (a.distance_km ?? 0) - (b.distance_km ?? 0));
    return filtered;
  }, [coaches, priceRange, ageGroup, focus, radiusKm]);

  const academyPoints = useMemo(() => {
    const withDistance = academies
      .filter((a) => typeof a.location_lat === "number" && typeof a.location_lng === "number")
      .map((a) => {
        const distance_km = focus
          ? Number(haversineKm(focus.lat, focus.lng, a.location_lat, a.location_lng).toFixed(1))
          : null;
        return { ...a, lat: a.location_lat, lng: a.location_lng, distance_km };
      });
    const filtered = (focus && radiusKm)
      ? withDistance.filter((a) => a.distance_km != null && a.distance_km <= radiusKm)
      : withDistance;
    if (focus && radiusKm) filtered.sort((a, b) => (a.distance_km ?? 0) - (b.distance_km ?? 0));
    return filtered;
  }, [academies, focus, radiusKm]);

  const visibleCoachPoints  = kind === "academies" ? [] : points;
  const visibleAcademyPoints = kind === "coaches" ? [] : academyPoints;
  const allVisible = [...visibleCoachPoints, ...visibleAcademyPoints];

  const center = allVisible.length ? [allVisible[0].lat, allVisible[0].lng] : [42.6977, 23.3219]; // Sofia
  const withoutCoords = (kind === "academies" ? 0 : (coaches.length - points.length))
                      + (kind === "coaches" ? 0 : (academies.length - academyPoints.length));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="header-blur">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between p-4">
          <Link to={user ? "/" : "/login"} className="flex items-center">
            <Logo size={26} wordmark />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <Link to="/" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> {t("dashboard")}
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors">{t("login")}</Link>
                <Link to="/register" className="btn-primary px-3 py-1.5 text-xs">{t("register")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="px-4 py-6 max-w-[1600px] mx-auto w-full">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Карта" : "Map"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
              <MapIcon className="w-7 h-7 text-pitch" />
              {t("coachesNearYou")}
            </h1>
          </div>
          <div className="inline-flex border border-white/10 p-0.5">
            <Link to="/find-coaches" className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white/55 hover:text-pitch flex items-center gap-1" data-testid="view-grid">
              <LayoutGrid className="w-3 h-3" /> {t("gridView")}
            </Link>
            <span className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-pitch text-white flex items-center gap-1" data-testid="view-map">
              <MapIcon className="w-3 h-3" /> {t("mapView")}
            </span>
          </div>
        </div>

        {/* Kind toggle */}
        <div className="inline-flex border border-white/10 p-0.5 mb-4" data-testid="map-kind-toggle">
          {[
            { id: "coaches",   label_bg: "Треньори",  label_en: "Coaches" },
            { id: "academies", label_bg: "Академии", label_en: "Academies" },
            { id: "both",      label_bg: "Всички",   label_en: "All" },
          ].map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest transition-colors ${kind === k.id ? "bg-pitch text-white" : "text-white/55 hover:text-pitch"}`}
              data-testid={`map-kind-${k.id}`}
            >
              {lang === "bg" ? k.label_bg : k.label_en}
            </button>
          ))}
        </div>

        {/* Coaches near me — gold radius filter */}
        <div className="surface-elevated p-4 mb-4 rounded-lg" data-testid="nearby-bar">
          <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-pitch/12 border border-pitch/35 flex items-center justify-center flex-shrink-0">
                <Locate className="w-4 h-4 text-pitch" />
              </div>
              <div className="min-w-0">
                <div className="label-mono">// {lang === "bg" ? "Намери близо до теб" : "Find near you"}</div>
                <div className="font-oswald uppercase tracking-tight text-base sm:text-lg leading-tight">
                  {focus && radiusKm
                    ? (lang === "bg" ? `${points.length} треньора в ${radiusKm} км` : `${points.length} coach${points.length === 1 ? "" : "es"} within ${radiusKm} km`)
                    : (lang === "bg" ? "Треньори около теб" : "Coaches near me")}
                </div>
                {focus && (
                  <div className="text-[11px] text-white/45 font-mono flex items-center gap-1 mt-0.5 truncate">
                    <MapPin className="w-3 h-3 text-pitch flex-shrink-0" /> {focus.label}
                  </div>
                )}
                {geoErr && <div className="text-[11px] text-red-400 font-mono mt-1" data-testid="nearby-geo-error">{geoErr}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={useMyLocation}
                disabled={geoBusy}
                className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="nearby-use-gps"
              >
                <Locate className={`w-3.5 h-3.5 ${geoBusy ? "animate-spin" : ""}`} />
                {geoBusy
                  ? (lang === "bg" ? "Откриване..." : "Locating...")
                  : (lang === "bg" ? "Моята локация" : "Use my GPS")}
              </button>
              {(focus || radiusKm) && (
                <button
                  type="button"
                  onClick={clearNearby}
                  className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-1.5"
                  data-testid="nearby-clear"
                >
                  <X className="w-3.5 h-3.5" /> {lang === "bg" ? "Изчисти" : "Clear"}
                </button>
              )}
            </div>
          </div>

          {/* Radius chip slider */}
          <div className="flex items-center gap-2 mt-3 flex-wrap overflow-x-auto scroll-thin">
            <span className="label-mono text-[10px] flex-shrink-0">{lang === "bg" ? "Радиус" : "Radius"}</span>
            {[
              { km: null, label_bg: "Всички", label_en: "All" },
              { km: 5,  label_bg: "5 км",  label_en: "5 km" },
              { km: 10, label_bg: "10 км", label_en: "10 km" },
              { km: 25, label_bg: "25 км", label_en: "25 km" },
              { km: 50, label_bg: "50 км", label_en: "50 km" },
            ].map((r) => {
              const active = radiusKm === r.km;
              const disabled = r.km != null && !focus;
              return (
                <button
                  key={r.km ?? "all"}
                  type="button"
                  disabled={disabled}
                  onClick={() => setRadiusKm(r.km)}
                  title={disabled ? (lang === "bg" ? "Първо избери локация" : "Pick a location first") : undefined}
                  className={`chip-badge flex-shrink-0 transition-all ${active ? "" : "opacity-50 hover:opacity-100"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                  style={active ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                  data-testid={`nearby-radius-${r.km ?? "all"}`}
                >
                  {lang === "bg" ? r.label_bg : r.label_en}
                </button>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="surface-1 p-4 space-y-3 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Coach name search */}
            <div className="relative">
              <SearchIcon className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "bg" ? "Търси име на треньор..." : "Search by coach name..."}
                className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none"
                data-testid="map-search"
              />
            </div>

            {/* Location autocomplete (Nominatim) */}
            <div className="relative" ref={locInputRef}>
              <MapPin className="w-4 h-4 text-pitch absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={focus && !locQuery ? focus.label : locQuery}
                onChange={(e) => { setLocQuery(e.target.value); setShowSuggest(true); setFocus(null); }}
                onFocus={() => setShowSuggest(true)}
                placeholder={lang === "bg" ? "Търси локация (напр. Младост 1, София)" : "Search location (e.g. Younge street)"}
                className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-20 py-2.5 focus:border-pitch outline-none"
                data-testid="map-location-search"
              />
              <button
                type="button"
                onClick={useMyLocation}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/45 hover:text-pitch transition-colors flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
                title={lang === "bg" ? "Моята локация" : "My location"}
                data-testid="map-use-my-location"
              >
                <Locate className="w-3.5 h-3.5" />
              </button>
              {(focus || locQuery) && (
                <button
                  type="button"
                  onClick={() => { setFocus(null); setLocQuery(""); setShowSuggest(false); }}
                  className="absolute right-9 top-1/2 -translate-y-1/2 text-white/35 hover:text-red-400"
                  title={lang === "bg" ? "Изчисти" : "Clear"}
                  data-testid="map-clear-location"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {showSuggest && locQuery.length >= 2 && (
                <div className="absolute z-30 left-0 right-0 mt-1 glass-elevated max-h-72 overflow-y-auto scroll-thin" data-testid="map-location-suggestions">
                  {locLoading && <div className="p-3 text-xs font-mono text-white/40">{lang === "bg" ? "Търсене..." : "Searching..."}</div>}
                  {!locLoading && locSuggestions.length === 0 && <div className="p-3 text-xs font-mono text-white/40">{lang === "bg" ? "Няма резултати" : "No results"}</div>}
                  {locSuggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setFocus({ lat: s.lat, lng: s.lng, label: s.short });
                        setLocQuery("");
                        setShowSuggest(false);
                        if (!radiusKm) setRadiusKm(25);
                      }}
                      className="w-full text-left p-3 hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors"
                      data-testid={`map-suggestion-${s.id}`}
                    >
                      <div className="font-oswald uppercase tracking-tight text-sm flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-pitch flex-shrink-0" /> {s.short}
                      </div>
                      <div className="text-[10px] font-mono text-white/35 mt-0.5 line-clamp-1">{s.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Specialty chip filters */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSpecialty("")}
              className={`chip-badge ${!specialty ? "" : "opacity-50 hover:opacity-100"}`}
              style={!specialty ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
              data-testid="map-spec-all"
            >
              {lang === "bg" ? "Всички" : "All"}
            </button>
            {SPECIALTIES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSpecialty(specialty === s.id ? "" : s.id)}
                className={`chip-badge ${specialty === s.id ? "" : "opacity-50 hover:opacity-100"}`}
                style={specialty === s.id ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                data-testid={`map-spec-${s.id === "general" ? "general" : s.id}`}
              >
                {lang === "bg" ? s.label_bg : s.label_en}
              </button>
            ))}
          </div>

          {/* Price + age group filters (compact secondary row) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-mono mb-1.5">{lang === "bg" ? "Цена" : "Price"}</div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "", label_bg: "Всички", label_en: "All" },
                  { id: "under30", label_bg: "< €30", label_en: "< €30" },
                  { id: "30-60", label_bg: "€30–60", label_en: "€30–60" },
                  { id: "60-100", label_bg: "€60–100", label_en: "€60–100" },
                  { id: "100plus", label_bg: "€100+", label_en: "€100+" },
                ].map((p) => (
                  <button
                    key={p.id || "all"}
                    type="button"
                    onClick={() => setPriceRange(p.id)}
                    className={`chip-badge ${priceRange === p.id ? "" : "opacity-50 hover:opacity-100"}`}
                    style={priceRange === p.id ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                    data-testid={`map-price-${p.id || "all"}`}
                  >
                    {lang === "bg" ? p.label_bg : p.label_en}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="label-mono mb-1.5">{lang === "bg" ? "Възрастова група" : "Age group"}</div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "", label_bg: "Всички", label_en: "All" },
                  { id: "kids", label_bg: "Деца (≤12)", label_en: "Kids (≤12)" },
                  { id: "youth", label_bg: "Младежи (13–17)", label_en: "Youth (13–17)" },
                  { id: "adult", label_bg: "Възрастни (18+)", label_en: "Adults (18+)" },
                  { id: "pro", label_bg: "Про", label_en: "Pro" },
                ].map((a) => (
                  <button
                    key={a.id || "all"}
                    type="button"
                    onClick={() => setAgeGroup(a.id)}
                    className={`chip-badge ${ageGroup === a.id ? "" : "opacity-50 hover:opacity-100"}`}
                    style={ageGroup === a.id ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                    data-testid={`map-age-${a.id || "all"}`}
                  >
                    {lang === "bg" ? a.label_bg : a.label_en}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs font-mono pt-1">
            <span className="label-mono">
              {points.length} {lang === "bg" ? "видими" : "visible"}
            </span>
            {withoutCoords > 0 && (
              <span className="text-white/40">
                + {withoutCoords} {lang === "bg" ? "без координати" : "without coordinates"}
              </span>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="surface-elevated overflow-hidden" style={{ height: "70vh", minHeight: 520 }} data-testid="coaches-map-container">
          {loading ? (
            <div className="h-full flex items-center justify-center"><div className="pitch-loader"><div className="ball" /></div></div>
          ) : points.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <MapIcon className="w-12 h-12 text-white/20 mb-3" />
              <div className="font-oswald uppercase text-2xl tracking-tight">{lang === "bg" ? "Все още няма треньори на картата" : "No coaches on the map yet"}</div>
              <div className="text-sm text-white/40 mt-2 max-w-md font-mono">
                {lang === "bg"
                  ? "Треньорите могат да добавят локация в Настройки → Профил, за да се появят тук."
                  : "Coaches can add a location in Settings → Profile to appear here."}
              </div>
              <Link to="/find-coaches" className="btn-primary mt-5 px-4 py-2 text-xs inline-flex items-center gap-1.5">
                <LayoutGrid className="w-3.5 h-3.5" /> {lang === "bg" ? "Виж списък" : "Open grid"}
              </Link>
            </div>
          ) : (
            <MapContainer center={center} zoom={11} className="h-full w-full" style={{ background: "#0A1628" }}>
              <TileLayer
                attribution='&copy; <a href="https://carto.com">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <FitToBounds points={allVisible} focus={focus} radiusKm={radiusKm} />
              {focus && radiusKm && (
                <Circle
                  center={[focus.lat, focus.lng]}
                  radius={radiusKm * 1000}
                  pathOptions={{
                    color: "#3B82F6",
                    weight: 1.5,
                    opacity: 0.85,
                    fillColor: "#3B82F6",
                    fillOpacity: 0.06,
                    dashArray: "6,8",
                  }}
                />
              )}
              {focus && (
                <Marker
                  position={[focus.lat, focus.lng]}
                  icon={L.divIcon({
                    className: "otp-focus-pin",
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                    html: '<div class="otp-focus-inner"><span class="otp-focus-ring"></span><span class="otp-focus-dot"></span></div>',
                  })}
                  interactive={false}
                />
              )}
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                maxClusterRadius={50}
                iconCreateFunction={(cluster) => L.divIcon({
                  html: `<div class="otp-cluster-inner"><span class="otp-cluster-ring"></span><span class="otp-cluster-count">${cluster.getChildCount()}</span></div>`,
                  className: "otp-cluster",
                  iconSize: L.point(44, 44, true),
                })}
              >
                {visibleAcademyPoints.map((a) => (
                <Marker key={`a-${a.id}`} position={[a.lat, a.lng]} icon={buildAcademyIcon(!!a.is_pro_academy)}>
                  <Popup className="tactiq-popup" closeButton={false}>
                    <div className="w-[260px]">
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 flex-shrink-0 rounded-xl bg-pitch/10 border border-pitch/40 flex items-center justify-center overflow-hidden">
                          {a.academy_logo ? (
                            <AuthImage src={a.academy_logo} className="w-full h-full object-cover" />
                          ) : (
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#FFB300" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="9" width="18" height="12" /><path d="M12 9V4l9 5" /><path d="M12 4l-9 5" /></svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-oswald uppercase tracking-tight text-base text-white truncate">{a.academy_name || a.name}</div>
                          <div className="flex items-center gap-1.5 text-xs mt-0.5 font-mono text-white/55">
                            <span className="text-pitch">{a.coaches_count || 0}</span> {lang === "bg" ? "треньора" : "coaches"}
                          </div>
                          {a.city && (
                            <div className="text-[11px] text-white/55 font-mono flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3 text-pitch" /> {a.city}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/8">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="chip-badge gold">ACADEMY</span>
                          {a.distance_km != null && (
                            <span className="chip-badge" data-testid={`map-academy-distance-${a.id}`}>
                              <Locate className="w-3 h-3 inline mr-0.5" />
                              {a.distance_km < 1 ? `${Math.round(a.distance_km * 1000)} м` : `${a.distance_km} км`}
                            </span>
                          )}
                        </div>
                        <Link to={`/academy/${a.id}`} className="btn-primary px-3 py-1.5 text-[11px] inline-flex items-center gap-1" data-testid={`map-academy-cta-${a.id}`}>
                          <Sparkles className="w-3 h-3" /> {t("viewProfile")}
                        </Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              </MarkerClusterGroup>

              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                maxClusterRadius={50}
                iconCreateFunction={(cluster) => L.divIcon({
                  html: `<div class="otp-cluster-inner"><span class="otp-cluster-ring"></span><span class="otp-cluster-count">${cluster.getChildCount()}</span></div>`,
                  className: "otp-cluster",
                  iconSize: L.point(44, 44, true),
                })}
              >
              {visibleCoachPoints.map((c) => (
                <Marker key={c.id} position={[c.lat, c.lng]} icon={buildIcon(!!c.is_pro)}>
                  <Popup className="tactiq-popup" closeButton={false}>
                    <div className="w-[260px]">
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 flex-shrink-0 relative">
                          {c.photo_url ? (
                            <AuthImage src={c.photo_url} className="w-full h-full object-cover rounded-full border border-pitch/50"
                              fallback={<div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-xl">{c.name?.[0]}</div>} />
                          ) : (
                            <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-xl">{c.name?.[0]}</div>
                          )}
                          {(c.badges || []).includes("verified") && (
                            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-pitch text-white rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-[#0A1628]">✓</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-oswald uppercase tracking-tight text-base text-white truncate">{c.name}</div>
                          <div className="flex items-center gap-1.5 text-xs mt-0.5">
                            <Star className="w-3 h-3 text-pitch fill-pitch" />
                            <span className="font-mono text-white">{(c.rating || 0).toFixed(1)}</span>
                            <span className="text-white/40 font-mono">· {c.review_count || 0}</span>
                          </div>
                          {c.city && (
                            <div className="text-[11px] text-white/55 font-mono flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3 text-pitch" /> {c.city}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2.5">
                        {c.specialty && <span className="chip-badge">{SPECIALTY_LABEL(c.specialty, lang)}</span>}
                        {c.instant_book_enabled && (
                          <span className="chip-badge gold flex items-center gap-1" data-testid={`map-instant-${c.id}`}>
                            <Zap className="w-3 h-3" /> INSTANT
                          </span>
                        )}
                        {c.distance_km != null && (
                          <span className="chip-badge gold" data-testid={`map-distance-${c.id}`}>
                            <Locate className="w-3 h-3 inline mr-0.5" />
                            {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)} м` : `${c.distance_km} км`}
                          </span>
                        )}
                        {(c.badges || []).slice(0, 2).map((b) => (
                          <span key={b} className={`chip-badge ${b === "uefa_pro" ? "gold" : b === "top_rated" ? "amber" : b === "elite" ? "platinum" : ""}`}>
                            {b.toUpperCase().replace("_", " ")}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/8">
                        <div>
                          <div className="label-mono">{lang === "bg" ? "От" : "From"}</div>
                          <div className="font-oswald text-xl text-pitch text-glow-emerald leading-none mt-0.5">
                            {c.hourly_rate != null ? `€${c.hourly_rate}` : "—"}
                            {c.hourly_rate != null && <span className="text-[10px] text-white/40 ml-1">/{lang === "bg" ? "ч" : "h"}</span>}
                          </div>
                        </div>
                        <Link to={`/coach/${c.id}`} className="btn-primary px-3 py-1.5 text-[11px] inline-flex items-center gap-1" data-testid={`map-coach-cta-${c.id}`}>
                          <Sparkles className="w-3 h-3" /> {t("viewProfile")}
                        </Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}
